import axios from 'axios';

import {AuthSource} from 'enums';
import {
    LoginData, User, UserSummary, UserSettings, UsersFilters, UserAuditFilters, UserAudit, CursorPaginatedDBResponse, UsersAvailabilityData
} from 'types';
import {platformUtils, userAuthUtils} from 'utils';

import {API_URL} from './constants';

const login = (data: LoginData) =>
    axios.post(`${API_URL}/auth/login`, {...data, ...platformUtils.getPlatformInfo()}, {withCredentials: true})
        .then((res) => res && res.data ? res.data : null);

const loginByAuthToken = async (source: string, token: string) => {
    const res = await axios.post(`${API_URL}/auth/login/auth-token`, {source, token}, {withCredentials: true});
    return res.data;
};

const verifyMfaEmail = (accessToken: string, code: number) =>
    axios.post(`${API_URL}/auth/mfa/verify/email`, {code}, {
        withCredentials: true,
        headers: {Authorization: `Bearer ${accessToken}`}
    }).then((res) => res && res.data ? res.data : null);

const refreshAccessToken = () => axios.post(`${API_URL}/auth/refresh-token`, null, {withCredentials: true})
    .then((res) => res && res.data ? res.data : null);

const checkRefreshTokenExistence = () => axios.post(`${API_URL}/auth/refresh-token/exist`, null, {withCredentials: true})
    .then((res) => res && res.data ? res.data : null);

const logout = () => axios.post(`${API_URL}/auth/logout`, null, {withCredentials: true});

const getUser = (eid: number): Promise<User> => axios.get(`${API_URL}/users/${eid}`)
    .then((res) => res && res.data ? res.data : null);

const getProfile = () => axios.post(`${API_URL}/users/profile`)
    .then((res) => res && res.data ? res.data : null);

const setAvailable = async (isAvailable: boolean) => {
    const res = await axios.post(`${API_URL}/users/profile/available`, {isAvailable});
    return res.data;
};

const setNotificationsEnabled = async (enabled: boolean) => {
    const res = await axios.put(`${API_URL}/users/profile/notifications-enabled`, {enabled});
    return res.data;
};

const getUsers = async (filters?: UsersFilters): Promise<{users: User[], count: number, isMaxUsersCountExceeded: boolean}> => {
    const res = await axios.post(`${API_URL}/users`, {filters});
    return res.data;
};

const getUsersCount = async (): Promise<{count: number}> => {
    const res = await axios.post(`${API_URL}/users/count`);
    return res.data;
};

const getUsersSummary = async (filters?: {activated: boolean, groupId?: string}): Promise<UserSummary[]> => {
    const res = await axios.post(`${API_URL}/users/summary`, {filters});
    return res.data;
};

const getAgentUsersSummary = async (filters?: {activated: boolean, groupId?: string}): Promise<UserSummary[]> => {
    const res = await axios.post(`${API_URL}/users/summary/agents`, {filters});
    return res.data;
};

const createUserWithPassword = (user: any) => axios.post(`${API_URL}/users/with-password`, user)
    .then((res) => res && res.data ? res.data : null);

const createUserWithEmailConfirmation = (user: any) => axios.post(`${API_URL}/users/with-email-confirmation`, user)
    .then((res) => res && res.data ? res.data : null);

const updateUser = (eid: number, user: any): Promise<User> => axios.put(`${API_URL}/users/${eid}`, user)
    .then((res) => res && res.data ? res.data : null);

const updateUserPassword = (user: any): Promise<User> => axios.put(`${API_URL}/users/password`, user)
    .then((res) => res && res.data ? res.data : null);

const removeUser = (eid: number) => axios.delete(`${API_URL}/users/${eid}`)
    .then((res) => res && res.data ? res.data : null);

const getSettings = async (): Promise<UserSettings> => {
    const res = await axios.post(`${API_URL}/user/settings`);
    return res.data;
};

const getCurrentAppointment = async () => {
    const res = await axios.post(`${API_URL}/user/current-appointment`);
    return res.data;
};

const startCurrentAppointment = async () => {
    const res = await axios.post(`${API_URL}/user/start-current-appointment`);
    return res.data;
};

const removeIntegration = async (source: AuthSource) => {
    const res = await axios.delete(`${API_URL}/users/integration/${source}`);
    return res.data;
};

const setIntegration = async (source: AuthSource, credentials: any) => {
    const res = await axios.post(`${API_URL}/users/integration`, {source, credentials});
    return res.data;
};

const validateIntegrationCredentials = async ({source, credentials}: {source: AuthSource, credentials: any}) => {
    const res = await axios.post(`${API_URL}/users/integration/validate-credentials`, {source, credentials});
    return res.data;
};

const refreshAuthOnAccountActivation = async () => {
    const res = await axios.post(`${API_URL}/auth/refresh-auth-on-activation`, null, {withCredentials: true});
    return res.data;
};

const getOneTimeToken = async (source: AuthSource, credentials: any) => {
    const res = await axios.post(`${API_URL}/auth/external/auth-token`, {source, credentials});
    return res.data;
};

const updateNotificationToken = async (token: string) => {
    const res = await axios.put(`${API_URL}/users/${userAuthUtils.getLocalUser().eid}/notification-token`, {token});
    return res.data;
};

const getUserAudit = async (
    filters: UserAuditFilters,
    pagination: {page: number, pageSize: number}
): Promise<{items: UserAudit[], count: number}> => {
    const res = await axios.post(`${API_URL}/users/audit`, {filters, pagination});
    return res.data;
};

const getUserAuditChunkData = async (
    filters: UserAuditFilters,
    {next, limit}: {next: string, limit: number}
): Promise<CursorPaginatedDBResponse<UserAudit>> => {
    const response = await axios.post(`${API_URL}/users/audit/chunk`, {filters, next, limit});
    return response.data;
};

const getUsersAvailability = async (groupId?: string): Promise<UsersAvailabilityData> => {
    const res = await axios.post(`${API_URL}/users/availability`, {groupId});
    return res.data;
};

const disconnectActiveSession = async (socketId: string): Promise<void> => {
    const res = await axios.post(`${API_URL}/users/disconnect`, {socketId}, {withCredentials: true});
    return res.data;
};

export const userApi = {
    login,
    loginByAuthToken,
    logout,
    verifyMfaEmail,
    getUser,
    getProfile,
    setAvailable,
    getUsers,
    getUsersCount,
    getUsersSummary,
    getAgentUsersSummary,
    createUserWithPassword,
    createUserWithEmailConfirmation,
    updateUser,
    updateUserPassword,
    removeUser,
    getSettings,
    removeIntegration,
    getCurrentAppointment,
    startCurrentAppointment,
    setIntegration,
    refreshAccessToken,
    checkRefreshTokenExistence,
    validateIntegrationCredentials,
    getOneTimeToken,
    refreshAuthOnAccountActivation,
    updateNotificationToken,
    setNotificationsEnabled,
    getUserAudit,
    getUsersAvailability,
    getUserAuditChunkData,
    disconnectActiveSession
};
